import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteIcon from '@mui/icons-material/Delete';
import JiraStatusBlock from './components/JiraStatusBlock';
import JiraAuthModal from './components/JiraAuthModal';
import JiraFilters from './components/JiraFilters';
import JiraIssuesList from './components/JiraIssuesList';
import JiraPagination from './components/JiraPagination';
import JiraProjectsList from './components/JiraProjectsList';
import JiraDeleteConfirmDialog from './components/JiraDeleteConfirmDialog';
import {
  JiraCredentials,
  JiraFilter,
  JiraFilterResponse,
  JiraProject,
  JiraIssuesPreviewResponse,
  JiraIssue,
} from './types';
import {
  updateJiraCredentials,
  getJiraFilter,
  getJiraProjects,
  getJiraIssuesPreview,
  getJiraSavedIssues,
  saveJiraIssues,
  syncJiraIssues,
  deleteJiraIssues,
} from './api';
import { jiraAuthStorage } from './utils/jiraAuthStorage';
import { useSnackbar } from 'components/GlobalSnackbar';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserInfo } from '../../redux/actions';
import { PageShell } from 'components/AppShell';
import { USE_NEW_SHELL } from 'core/flags';
import LegacyJiraPage from './JiraPage.legacy';

const JiraPageNew: React.FC = () => {
  const dispatch = useDispatch();
  const showSnackbar = useSnackbar();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [jiraBaseUrl, setJiraBaseUrl] = useState<string | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currProject = useSelector((state: any) => state.reducer.project.id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Состояние вкладок
  const [activeTab, setActiveTab] = useState<'projects' | 'saved'>('projects');

  const [filterResponse, setFilterResponse] = useState<JiraFilterResponse | null>(null);
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [showProjects, setShowProjects] = useState(true);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');

  const [issuesPreview, setIssuesPreview] = useState<JiraIssuesPreviewResponse | null>(null);
  const [selectedProjectKey, setSelectedProjectKey] = useState<string | null>(null);
  const [savedIssues, setSavedIssues] = useState<JiraIssue[]>([]);
  const [selectedIssueKeys, setSelectedIssueKeys] = useState<string[]>([]);
  const [selectedSavedIssueKeys, setSelectedSavedIssueKeys] = useState<string[]>([]);
  const [isSavingIssues, setIsSavingIssues] = useState(false);
  const [isSyncingIssues, setIsSyncingIssues] = useState(false);
  const [isDeletingIssues, setIsDeletingIssues] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const [currentFilter, setCurrentFilter] = useState<JiraFilter>({
    projectKey: '',
  });

  useEffect(() => {
    loadAuthStatus();
  }, []);

  useEffect(() => {
    if (isAuthenticated && currProject) {
      loadData(currProject);
    }
  }, [isAuthenticated, currProject]);

  const loadAuthStatus = () => {
    const authData = jiraAuthStorage.get();
    setIsAuthenticated(authData.is_authenticated);
    setJiraBaseUrl(authData.jira_base_url);
  };

  const loadData = async (projectId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await getJiraFilter(projectId);
      if (!jiraAuthStorage.get().is_authenticated) return;
      setFilterResponse(response);

      // Загружаем список проектов для вкладки "Проекты"
      const projectsList = await getJiraProjects();
      if (!jiraAuthStorage.get().is_authenticated) return;
      setProjects(projectsList);

      // Если есть сохраненный фильтр, применяем его и загружаем задачи
      if (response.filter !== null) {
        setCurrentFilter(response.filter);
        setSelectedProjectKey(response.project.key);
        setShowProjects(false);

        // Применяем сохраненный фильтр и загружаем задачи
        const issuesResponse = await getJiraIssuesPreview({
          project_id: projectId,
          offset: 0,
          limit: 50,
          jira_project_key: response.project.key,
          status: response.filter.status || undefined,
          reporter: response.filter.reporter || undefined,
          assignee: response.filter.assignee || undefined,
        });
        if (!jiraAuthStorage.get().is_authenticated) return;
        setIssuesPreview(issuesResponse);

        // Загружаем сохраненные задачи
        const saved = await getJiraSavedIssues(projectId);
        if (!jiraAuthStorage.get().is_authenticated) return;
        setSavedIssues(saved);
      } else {
        setSavedIssues([]);
        setShowProjects(true);
      }
    } catch (err) {
      if (jiraAuthStorage.get().is_authenticated) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки данных');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const clearJiraState = () => {
    setFilterResponse(null);
    setProjects([]);
    setShowProjects(true);
    setIssuesPreview(null);
    setSelectedProjectKey(null);
    setSavedIssues([]);
    setCurrentFilter({ projectKey: '' });
    setSelectedIssueKeys([]);
    setSelectedSavedIssueKeys([]);
    setError(null);
  };

  const handleSubmitCredentials = async (credentials: JiraCredentials) => {
    await updateJiraCredentials(credentials);

    const isLogout = !credentials.jira_base_url && !credentials.jira_token;

    // Сохраняем статус авторизации в localStorage
    jiraAuthStorage.set(
      isLogout
        ? {
            is_authenticated: false,
          }
        : {
            is_authenticated: true,
            jira_base_url: credentials.jira_base_url,
          }
    );

    if (isLogout) {
      // Сразу сбрасываем UI и состояние, чтобы не дергать loadData и не показывать «подключено»
      setIsAuthenticated(false);
      setJiraBaseUrl(undefined);
      clearJiraState();
      await (dispatch as (action: ReturnType<typeof fetchUserInfo>) => Promise<unknown>)(
        fetchUserInfo()
      );
    } else {
      loadAuthStatus();
    }
  };

  const handleFilterChange = (filter: JiraFilter) => {
    setCurrentFilter(filter);
  };

  const handleApplyFilter = async () => {
    if (!selectedProjectKey) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getJiraIssuesPreview({
        project_id: currProject,
        offset: 0,
        limit: 50,
        jira_project_key: selectedProjectKey,
        status: currentFilter.status,
        reporter: currentFilter.reporter,
        assignee: currentFilter.assignee,
      });

      setIssuesPreview(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка применения фильтров');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (newOffset: number) => {
    if (!selectedProjectKey) return;

    try {
      setLoading(true);
      const response = await getJiraIssuesPreview({
        project_id: currProject,
        offset: newOffset,
        limit: 50,
        jira_project_key: selectedProjectKey,
        status: currentFilter.status,
        reporter: currentFilter.reporter,
        assignee: currentFilter.assignee,
      });
      setIssuesPreview(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки страницы');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSelectedIssues = async () => {
    if (!selectedProjectKey || !currProject || selectedIssueKeys.length === 0) return;

    try {
      setIsSavingIssues(true);
      setError(null);

      await saveJiraIssues({
        project_id: currProject,
        keys: selectedIssueKeys,
      });

      // Обновляем сохраненные задачи после сохранения
      const saved = await getJiraSavedIssues(currProject);
      setSavedIssues(saved);
      showSnackbar({ message: 'Задачи сохранены в контекст', severity: 'success' });
    } catch (err) {
      showSnackbar({
        message: err instanceof Error ? err.message : 'Ошибка сохранения задач',
        severity: 'error',
      });
    } finally {
      setIsSavingIssues(false);
    }
  };

  const handleTabChange = async (_event: React.SyntheticEvent, newValue: 'projects' | 'saved') => {
    setActiveTab(newValue);
    setError(null);
    // Сбрасываем выбранные задачи при смене вкладки
    if (newValue === 'saved') {
      setSelectedSavedIssueKeys([]);
      // Обновляем сохраненные задачи при переходе на вкладку
      if (currProject) {
        try {
          const saved = await getJiraSavedIssues(currProject);
          setSavedIssues(saved);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Ошибка загрузки сохраненных задач');
        }
      }
    } else {
      setSelectedIssueKeys([]);
    }
  };

  const handleProjectClick = async (project: JiraProject) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedProjectKey(project.key);

      const response = await getJiraIssuesPreview({
        project_id: currProject,
        offset: 0,
        limit: 50,
        jira_project_key: project.key,
      });

      setIssuesPreview(response);
      setCurrentFilter({
        projectKey: project.key,
      });
      setShowProjects(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки задач проекта');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToProjects = () => {
    setShowProjects(true);
    setIssuesPreview(null);
    setSelectedProjectKey(null);
    setCurrentFilter({
      projectKey: '',
    });
  };

  // Фильтрация проектов по поисковому запросу
  const filteredProjects = projects.filter((project) => {
    const query = projectSearchQuery.toLowerCase();
    return project.key.toLowerCase().includes(query) || project.name.toLowerCase().includes(query);
  });

  const handleSyncSelectedIssues = async () => {
    if (!currProject || selectedSavedIssueKeys.length === 0) return;

    try {
      setIsSyncingIssues(true);
      setError(null);

      await syncJiraIssues({
        project_id: currProject,
        keys: selectedSavedIssueKeys,
      });

      // Обновляем сохраненные задачи после синхронизации
      const saved = await getJiraSavedIssues(currProject);
      setSavedIssues(saved);
      setSelectedSavedIssueKeys([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка синхронизации задач');
    } finally {
      setIsSyncingIssues(false);
    }
  };

  const handleDeleteSelectedIssues = async () => {
    if (!currProject || selectedSavedIssueKeys.length === 0) return;

    try {
      setIsDeletingIssues(true);
      setError(null);

      await deleteJiraIssues({
        project_id: currProject,
        keys: selectedSavedIssueKeys,
      });

      // Обновляем сохраненные задачи после удаления
      const saved = await getJiraSavedIssues(currProject);
      setSavedIssues(saved);
      setSelectedSavedIssueKeys([]);
      setIsDeleteDialogOpen(false);
      showSnackbar({ message: 'Задачи удалены из контекста', severity: 'success' });
    } catch (err) {
      showSnackbar({
        message: err instanceof Error ? err.message : 'Ошибка удаления задач',
        severity: 'error',
      });
    } finally {
      setIsDeletingIssues(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    if (selectedSavedIssueKeys.length > 0) {
      setIsDeleteDialogOpen(true);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeletingIssues) {
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <PageShell
      title="Jira"
      subtitle={
        isAuthenticated && jiraBaseUrl
          ? `Подключено: ${jiraBaseUrl}`
          : 'Импортируйте проекты и задачи Jira в контекст'
      }
      actions={
        <JiraStatusBlock
          isAuthenticated={isAuthenticated}
          jiraBaseUrl={jiraBaseUrl}
          onClick={handleOpenModal}
        />
      }
      bodyScrolls={false}
      noContentPadding
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 2,
        }}
      >
        {isAuthenticated && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, flexShrink: 0 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Проекты и задачи" value="projects" />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Сохраненные в контекст задачи
                      {savedIssues.length > 0 && (
                        <Box
                          sx={{
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            borderRadius: '12px',
                            minWidth: 'auto',
                            height: 20,
                            px: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            lineHeight: 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {savedIssues.length}
                        </Box>
                      )}
                    </Box>
                  }
                  value="saved"
                  disabled={!filterResponse || filterResponse.filter === null}
                />
              </Tabs>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexShrink: 0 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  overflow: 'hidden',
                  minHeight: 0,
                }}
              >
                {/* Вкладка "Проекты" */}
                {activeTab === 'projects' && (
                  <>
                    {showProjects ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          overflow: 'hidden',
                          minHeight: 0,
                        }}
                      >
                        <Typography variant="h5" gutterBottom sx={{ flexShrink: 0 }}>
                          Выберите проект
                        </Typography>

                        <TextField
                          fullWidth
                          placeholder="Поиск по ключу или названию проекта..."
                          value={projectSearchQuery}
                          onChange={(e) => setProjectSearchQuery(e.target.value)}
                          InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                          }}
                          sx={{ mb: 3, flexShrink: 0 }}
                        />

                        <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                          <JiraProjectsList
                            projects={filteredProjects}
                            onProjectClick={handleProjectClick}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {issuesPreview && (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              flex: 1,
                              overflow: 'hidden',
                              minHeight: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 2,
                                flexShrink: 0,
                              }}
                            >
                              <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToProjects}
                              >
                                К проектам
                              </Button>
                              <Typography variant="h5">
                                Задачи проекта {selectedProjectKey}
                              </Typography>
                            </Box>

                            <JiraFilters
                              filter={currentFilter}
                              onChange={handleFilterChange}
                              onApply={handleApplyFilter}
                              onSaveSelected={handleSaveSelectedIssues}
                              isSavingSelected={isSavingIssues}
                              hasSelectedIssues={selectedIssueKeys.length > 0}
                            />

                            <Box
                              sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                mb: 2,
                              }}
                            >
                              <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                                <JiraIssuesList
                                  issues={issuesPreview.issues}
                                  jiraBaseUrl={jiraBaseUrl}
                                  showCheckboxes={true}
                                  onSelectionChange={setSelectedIssueKeys}
                                  initialSelectedKeys={selectedIssueKeys}
                                />
                              </Box>
                            </Box>

                            <Box sx={{ flexShrink: 0 }}>
                              <JiraPagination
                                offset={issuesPreview.offset}
                                limit={issuesPreview.limit}
                                total={issuesPreview.total}
                                onChange={handlePageChange}
                              />
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Вкладка "Сохраненные задачи" */}
                {activeTab === 'saved' && filterResponse && filterResponse.filter && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      overflow: 'hidden',
                      minHeight: 0,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<SyncIcon />}
                        onClick={handleSyncSelectedIssues}
                        disabled={selectedSavedIssueKeys.length === 0 || isSyncingIssues}
                      >
                        {isSyncingIssues ? 'Синхронизация...' : 'Синхронизировать'}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleOpenDeleteDialog}
                        disabled={selectedSavedIssueKeys.length === 0 || isDeletingIssues}
                      >
                        Удалить
                      </Button>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                      <JiraIssuesList
                        issues={savedIssues}
                        jiraBaseUrl={jiraBaseUrl}
                        showCheckboxes={true}
                        onSelectionChange={setSelectedSavedIssueKeys}
                        initialSelectedKeys={selectedSavedIssueKeys}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <JiraAuthModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitCredentials}
          isAuthenticated={isAuthenticated}
        />

        <JiraDeleteConfirmDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDeleteSelectedIssues}
          selectedCount={selectedSavedIssueKeys.length}
          isLoading={isDeletingIssues}
        />
      </Box>
    </PageShell>
  );
};

const JiraPage: React.FC = () => {
  if (!USE_NEW_SHELL) {
    return <LegacyJiraPage />;
  }
  return <JiraPageNew />;
};

export default JiraPage;
